import React, {
  useEffect,
  useMemo,
} from "react";

import {
  useParams,
} from "react-router-dom";

import {
  useAdminScope,
} from "../../context/AdminScopeContext";

import RetailPOSApplicationHost from "../../components/RetailStudio/RetailPOSApplicationHost";

import {
  createRetailPOSApplicationComposition,
} from "../../components/RetailStudio/retailPOSApplicationComposition";

import {
  createRetailPOSProductionInfrastructure,
} from "../../components/RetailStudio/retailPOSProductionInfrastructure";


/**
 * Production route boundary for Retail POS.
 *
 * Authorities:
 *
 * companyUuid
 *   = AdminScopeContext.effectiveCompanyUuid
 *
 * activityUuid
 *   = AdminScopeContext.effectiveActivityUuid
 *
 * React Router /pos/:activityUuid is the Activity scope ingress.
 * The route synchronizes that ingress into AdminScopeContext;
 * it is not a parallel Runtime authority.
 *
 * The route owns no Retail business logic and no publication,
 * product, session or checkout state.
 */
export default function ProductionPOSRoute() {
  const {
    activityUuid,
  } = useParams();

  const {
    effectiveCompanyUuid,
    effectiveActivityUuid,
    setSelectedActivity,
    clearSelectedActivity,
  } = useAdminScope();


  const productionInfrastructure =
    useMemo(
      () =>
        createRetailPOSProductionInfrastructure(),
      []
    );


  const composition =
    useMemo(
      () =>
        createRetailPOSApplicationComposition({
          publicationEngine:
            productionInfrastructure
              .publicationEngine,

          getVersion:
            productionInfrastructure
              .getVersion,

          listDocuments:
            productionInfrastructure
              .listDocuments,
        }),
      [
        productionInfrastructure,
      ]
    );


  const companyUuid =
    normalizeScopeUuid(
      effectiveCompanyUuid
    );

  const routeActivityUuid =
    normalizeScopeUuid(
      activityUuid
    );

  const scopedActivityUuid =
    normalizeScopeUuid(
      effectiveActivityUuid
    );


  useEffect(() => {
    if (!routeActivityUuid) {
      clearSelectedActivity();
      return;
    }

    if (
      scopedActivityUuid
      === routeActivityUuid
    ) {
      return;
    }

    setSelectedActivity({
      uuid: routeActivityUuid,
    });
  }, [
    routeActivityUuid,
    scopedActivityUuid,
    setSelectedActivity,
    clearSelectedActivity,
  ]);


  if (!companyUuid) {
    return (
      <div
        role="alert"
        aria-live="assertive"
      >
        Aucun scope société actif pour le POS.
      </div>
    );
  }


  if (!routeActivityUuid) {
    return (
      <div
        role="alert"
        aria-live="assertive"
      >
        Aucune activité POS valide dans la route.
      </div>
    );
  }


  if (
    !scopedActivityUuid
    || scopedActivityUuid
      !== routeActivityUuid
  ) {
    return (
      <div
        role="status"
        aria-live="polite"
      >
        Synchronisation du scope activité POS.
      </div>
    );
  }


  return (
    <RetailPOSApplicationHost
      composition={composition}
      companyUuid={companyUuid}
      activityUuid={scopedActivityUuid}
    />
  );
}


function normalizeScopeUuid(
  value
) {
  if (
    typeof value !== "string"
    || !value.trim()
  ) {
    return null;
  }

  return value.trim();
}
